import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import api from '../../../services/api';
import { API_PATHS } from '../constants';
import { apiTemplateToCard, sortTemplates } from '../utils';

async function requestWithFallback(paths, method, data) {
  let lastError;
  for (let i = 0; i < paths.length; i += 1) {
    const path = paths[i];
    try {
      if (method === 'get') return { response: await api.get(path), path };
      if (method === 'patch') return { response: await api.patch(path, data), path };
      if (method === 'post') return { response: await api.post(path, data), path };
      if (method === 'delete') return { response: await api.delete(path), path };
    } catch (err) {
      lastError = err;
      const status = err.response?.status;
      const canFallback = i < paths.length - 1 && (status === 404 || status === 405);
      if (!canFallback) break;
    }
  }
  throw lastError;
}

function formatLoadError(err) {
  const status = err.response?.status;
  const detail = err.response?.data?.error || err.response?.data?.detail || err.message;
  if (status === 404) {
    return 'Auto-reply API not found. Restart the backend after adding salesgro_sma.';
  }
  if (status === 403) {
    return detail || 'You do not have permission to manage social media automation.';
  }
  if (status) {
    return detail ? `${detail} (HTTP ${status})` : `Request failed (HTTP ${status})`;
  }
  return detail || 'Failed to load auto-reply configuration';
}

function applyTemplatesFromResponse(response, setTemplates, initialRef, settings) {
  const refreshedTemplates = sortTemplates(response.data?.templates || []).map((t) => apiTemplateToCard(t, settings));
  setTemplates(refreshedTemplates);
  if (initialRef?.current !== undefined && settings) {
    initialRef.current = JSON.stringify({ settings, templates: refreshedTemplates });
  }
  return refreshedTemplates;
}

export default function useSocialAutoReply({ connectedPages = null } = {}) {
  const [settings, setSettings] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [pages, setPages] = useState(connectedPages || []);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const initialRef = useRef(null);

  const loadConfig = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const requests = [requestWithFallback(API_PATHS.autoReply, 'get')];
      if (!connectedPages) {
        requests.push(api.get('/lms/integrations/facebook/pages/').catch(() => ({ data: { pages: [] } })));
      }
      const [configResult, pagesRes] = await Promise.all(requests);
      const configRes = configResult.response;
      const nextSettings = configRes.data?.settings || {};
      const nextTemplates = sortTemplates(configRes.data?.templates || []).map((t) => apiTemplateToCard(t, nextSettings));

      setSettings(nextSettings);
      setTemplates(nextTemplates);
      if (pagesRes?.data?.pages) setPages(pagesRes.data.pages);
      initialRef.current = JSON.stringify({ settings: nextSettings, templates: nextTemplates });
    } catch (err) {
      setError(formatLoadError(err));
    } finally {
      setLoading(false);
    }
  }, [connectedPages]);

  useEffect(() => {
    loadConfig();
  }, [loadConfig]);

  useEffect(() => {
    if (connectedPages) setPages(connectedPages);
  }, [connectedPages]);

  const isDirty = useMemo(() => {
    if (!initialRef.current || !settings) return false;
    return JSON.stringify({ settings, templates }) !== initialRef.current;
  }, [settings, templates]);

  const persistConfig = useCallback(async (nextSettings, nextTemplates) => {
    const systemTemplates = nextTemplates.filter((t) => t.isSystemDefault || t.templateType !== 'custom');
    const { response } = await requestWithFallback(
      API_PATHS.autoReply,
      'patch',
      {
        settings: nextSettings,
        templates: systemTemplates.map((t) => ({
          templateType: t.templateType,
          name: t.name,
          body: t.body,
          dmBody: t.dmBody,
          status: t.status,
          // Always send these so that re-adding a previously-removed node persists too
          createLead: t.createLead !== false,
          showAssignAgent: t.showAssignAgent !== false,
          enableVaanikAgent: !!t.enableVaanikAgent,
        })),
      },
    );
    const refreshedSettings = response.data?.settings || nextSettings;
    setSettings(refreshedSettings);
    return applyTemplatesFromResponse(response, setTemplates, initialRef, refreshedSettings);
  }, []);

  const saveAll = useCallback(async () => {
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      await persistConfig(settings, templates);
      setSuccess('Social media automation saved.');
    } catch (err) {
      setError(formatLoadError(err));
    } finally {
      setSaving(false);
    }
  }, [persistConfig, settings, templates]);

  const toggleTemplateStatus = useCallback(async (template) => {
    const nextStatus = template.status === 'Active' ? 'Paused' : 'Active';
    setSaving(true);
    setError('');
    try {
      if (template.templateType === 'custom') {
        await requestWithFallback(
          [`/sma/reply-templates/${template.id}/`, `/lms/integrations/facebook/reply-templates/${template.id}/`],
          'patch',
          { status: nextStatus },
        );
        await loadConfig();
      } else {
        const nextTemplates = templates.map((t) => (
          t.id === template.id ? { ...t, status: nextStatus } : t
        ));
        await persistConfig(settings, nextTemplates);
      }
      setSuccess(`Template ${nextStatus === 'Active' ? 'activated' : 'paused'}.`);
    } catch (err) {
      setError(formatLoadError(err));
    } finally {
      setSaving(false);
    }
  }, [loadConfig, persistConfig, settings, templates]);

  const saveTemplate = useCallback(async (templateIdOrType, updates) => {
    setSaving(true);
    setError('');
    try {
      const template = templates.find((t) => t.id === templateIdOrType || t.templateType === templateIdOrType);
      const { settingsPatch, ...templateUpdates } = updates || {};

      if (template?.templateType === 'custom') {
        await requestWithFallback(
          [`/sma/reply-templates/${template.id}/`, `/lms/integrations/facebook/reply-templates/${template.id}/`],
          'patch',
          {
            name: templateUpdates.name,
            body: templateUpdates.body,
            dmBody: templateUpdates.dmBody,
            status: templateUpdates.status,
            triggerChannel: templateUpdates.triggerChannel,
            keywords: templateUpdates.keywords,
            matchRule: templateUpdates.matchRule,
            createLead: templateUpdates.createLead,
            sortOrder: templateUpdates.sortOrder,
            isChannelDefault: templateUpdates.isChannelDefault,
            enableVaanikAgent: templateUpdates.enableVaanikAgent,
            showAssignAgent: templateUpdates.showAssignAgent,
          },
        );
        if (settingsPatch) setSettings((s) => ({ ...s, ...settingsPatch }));
        await loadConfig();
      } else {
        // Use both id and templateType for the map so the right template is always updated
        let nextTemplates = templates.map((t) => (
          (t.id === templateIdOrType || t.templateType === templateIdOrType)
            ? { ...t, ...templateUpdates }
            : t
        ));
        // The DM body for a comment_reply flow is stored in comment_reply.dm_body.
        // If the legacy comment_dm template still exists, keep it in sync so it
        // stays consistent until the user deletes it.
        if (template?.templateType === 'comment_reply' && templateUpdates.dmBody != null) {
          nextTemplates = nextTemplates.map((t) => (
            t.templateType === 'comment_dm' ? { ...t, body: templateUpdates.dmBody } : t
          ));
        }
        // Persist createLead / showAssignAgent changes via the detail endpoint so
        // both removing AND re-adding these nodes lands in the DB atomically before
        // persistConfig rewrites the whole batch.
        const createLeadChanged = templateUpdates.createLead !== undefined
          && (templateUpdates.createLead !== false) !== (template.createLead !== false);
        const showAssignChanged = templateUpdates.showAssignAgent !== undefined
          && (templateUpdates.showAssignAgent !== false) !== (template.showAssignAgent !== false);
        const enableVaanikChanged = templateUpdates.enableVaanikAgent !== undefined
          && !!templateUpdates.enableVaanikAgent !== !!template.enableVaanikAgent;
        if (template?.id && (createLeadChanged || showAssignChanged || enableVaanikChanged)) {
          const overridePatch = {};
          if (createLeadChanged) overridePatch.createLead = templateUpdates.createLead !== false;
          if (showAssignChanged) overridePatch.showAssignAgent = templateUpdates.showAssignAgent !== false;
          if (enableVaanikChanged) overridePatch.enableVaanikAgent = !!templateUpdates.enableVaanikAgent;
          await requestWithFallback(
            [`/sma/reply-templates/${template.id}/`, `/lms/integrations/facebook/reply-templates/${template.id}/`],
            'patch',
            overridePatch,
          );
        }
        // If setting as channel default, also handle isChannelDefault via PATCH for non-custom templates
        if (templateUpdates.isChannelDefault) {
          await requestWithFallback(
            [`/sma/reply-templates/${template.id}/`, `/lms/integrations/facebook/reply-templates/${template.id}/`],
            'patch',
            { isChannelDefault: true },
          );
        }
        const nextSettings = settingsPatch ? { ...settings, ...settingsPatch } : settings;
        if (settingsPatch) setSettings(nextSettings);
        await persistConfig(nextSettings, nextTemplates);
      }
      setSuccess('Template updated.');
    } catch (err) {
      setError(formatLoadError(err));
      throw err;
    } finally {
      setSaving(false);
    }
  }, [loadConfig, persistConfig, settings, templates]);

  const createTemplate = useCallback(async (payload) => {
    setSaving(true);
    setError('');
    try {
      const keywords = Array.isArray(payload.keywords)
        ? payload.keywords
        : String(payload.keywords || '').split(',').map((k) => k.trim()).filter(Boolean);

      const { response } = await requestWithFallback(API_PATHS.replyTemplates, 'post', {
        name: payload.name,
        body: payload.body,
        dmBody: payload.dmBody,
        triggerChannel: payload.triggerChannel,
        keywords,
        matchRule: payload.matchRule || 'contains_any',
        createLead: payload.createLead ?? true,
        status: payload.status || 'Active',
        isChannelDefault: payload.isChannelDefault ?? false,
        enableVaanikAgent: payload.enableVaanikAgent ?? false,
        showAssignAgent: payload.showAssignAgent !== false,
      });
      await loadConfig();
      setSuccess('Template created.');
      return apiTemplateToCard(response.data?.template || {}, settings);
    } catch (err) {
      setError(formatLoadError(err));
      throw err;
    } finally {
      setSaving(false);
    }
  }, [loadConfig, settings]);

  const deleteTemplate = useCallback(async (template) => {
    if (!template) return;
    // comment_dm is a legacy redundant template — always deletable.
    // For other system defaults: only block if not a user-designated channel default.
    const isBlockedSystemDefault = template.isSystemDefault
      && !template.isChannelDefault
      && template.templateType !== 'comment_dm';
    if (isBlockedSystemDefault) return;
    setSaving(true);
    setError('');
    try {
      await requestWithFallback(
        [`/sma/reply-templates/${template.id}/`, `/lms/integrations/facebook/reply-templates/${template.id}/`],
        'delete',
      );
      await loadConfig();
      setSuccess('Template deleted.');
    } catch (err) {
      setError(formatLoadError(err));
      throw err;
    } finally {
      setSaving(false);
    }
  }, [loadConfig]);

  const previewTemplate = useCallback(async (templateType, body, name) => {
    const { response } = await requestWithFallback(
      API_PATHS.testTemplate,
      'post',
      { templateType, body, name },
    );
    return response.data?.rendered || '';
  }, []);

  return {
    settings,
    setSettings,
    templates,
    pages,
    loading,
    saving,
    error,
    setError,
    success,
    setSuccess,
    isDirty,
    saveAll,
    toggleTemplateStatus,
    saveTemplate,
    createTemplate,
    deleteTemplate,
    previewTemplate,
    reload: loadConfig,
  };
}
